import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import {
  Badge,
  createTableColumn,
  DataGrid,
  DataGridBody,
  DataGridCell,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridRow,
  TableCellLayout,
  TableColumnDefinition,
  Text
} from '@fluentui/react-components';
import { ArrowTrendingDownFilled, ArrowTrendingFilled } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { ConceptCostHistory } from '../../../../../models/Entities/Contracts/ConceptCostHistory';
import { useConceptValueHistoryChartStyles } from './ConceptValueHistoryChart.Styles';

export interface ConceptValueHistoryChartProps {
  histories: ConceptCostHistory[];
  currency?: string | null;
}

const formatCost = (value: number, currency: string): string =>
  value.toLocaleString('es-AR', {
    style: 'currency',
    currency: (currency?.trim() || 'ARS').toUpperCase()
  });

const formatDate = (dateStr: string): string => {
  const d = new Date(dateStr);
  return Number.isNaN(d.getTime())
    ? dateStr
    : d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

interface HistoryRow {
  id: number;
  date: string;
  currency: string;
  previousValue: number;
  newValue: number;
  variancePercentage: number;
}

const ConceptValueHistoryChartComponent: React.FC<ConceptValueHistoryChartProps> = ({
  histories,
  currency
}) => {
  const { t } = useTranslation();
  const styles = useConceptValueHistoryChartStyles();
  const locale = globalThis.navigator.language || 'es-AR';
  const displayCurrency = (currency?.trim() || 'ARS').toUpperCase();

  // Sort ascending for chart, descending for grid
  const sortedAsc = useMemo(
    () =>
      histories
        .slice()
        .sort((a, b) => new Date(a.changeDate).getTime() - new Date(b.changeDate).getTime()),
    [histories]
  );

  const rows: HistoryRow[] = useMemo(
    () =>
      sortedAsc.map((h) => ({
        id: h.id,
        date: h.changeDate,
        currency: displayCurrency,
        previousValue: h.previousReferenceCost,
        newValue: h.newReferenceCost,
        variancePercentage: h.referenceCostVariancePercentage
      })).reverse(), // descending for grid (most recent first)
    [sortedAsc, displayCurrency]
  );

  // ECharts: line chart of newReferenceCost over time
  const chartOption = useMemo(() => {
    const dates = sortedAsc.map((h) => formatDate(h.changeDate));
    const values = sortedAsc.map((h) => h.newReferenceCost);
    const variances = sortedAsc.map((h) => h.referenceCostVariancePercentage);

    const minVal = Math.min(...values);
    const maxVal = Math.max(...values);
    const pad = maxVal === minVal ? 1 : (maxVal - minVal) * 0.15;

    return {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'axis',
        formatter: (params: any[]) => {
          const p = params[0];
          const idx = p.dataIndex;
          const variance = variances[idx];
          if (variance === 0) {
            return `${p.name}<br/>${p.marker} ${formatCost(p.value, displayCurrency)}`;
          }
          const varColor = variance > 0 ? '#20a34a' : '#c4314b';
          const varSign = variance > 0 ? '+' : '';
          const varStr = ` <span style="color:${varColor};font-weight:bold">(${varSign}${variance.toFixed(1)}%)</span>`;
          return `${p.name}<br/>${p.marker} ${formatCost(p.value, displayCurrency)}${varStr}`;
        }
      },
      grid: { left: 60, right: 20, top: 30, bottom: 50 },
      xAxis: {
        type: 'category',
        data: dates,
        axisLabel: { rotate: 35, fontSize: 11 }
      },
      yAxis: {
        type: 'value',
        min: minVal - pad,
        max: maxVal + pad,
        axisLabel: {
          formatter: (v: number) =>
            v.toLocaleString(locale, { maximumFractionDigits: 0 })
        }
      },
      dataZoom: [
        { type: 'inside', throttle: 50 },
        { type: 'slider', height: 18, bottom: 8 }
      ],
      series: [
        {
          name: t('conceptValueHistoryChart.series.referenceCost', 'Costo referencia'),
          type: 'line',
          smooth: true,
          symbol: 'circle',
          symbolSize: 7,
          color: '#247dbd',
          areaStyle: { opacity: 0.08 },
          lineStyle: { width: 2 },
          data: values,
          markPoint: {
            data: [
              { type: 'max', name: t('conceptValueHistoryChart.mark.max', 'Máximo') },
              { type: 'min', name: t('conceptValueHistoryChart.mark.min', 'Mínimo') }
            ]
          }
        }
      ]
    };
  }, [sortedAsc, displayCurrency, locale, t]);

  const columns: TableColumnDefinition<HistoryRow>[] = useMemo(
    () => [
      createTableColumn<HistoryRow>({
        columnId: 'date',
        compare: (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
        renderHeaderCell: () => t('conceptValueHistoryChart.columns.date', 'Fecha'),
        renderCell: (item) => (
          <TableCellLayout>
            <Text size={200}>{formatDate(item.date)}</Text>
          </TableCellLayout>
        )
      }),
      createTableColumn<HistoryRow>({
        columnId: 'currency',
        renderHeaderCell: () => t('conceptValueHistoryChart.columns.currency', 'Moneda'),
        renderCell: (item) => (
          <TableCellLayout>
            <Text size={200}>{item.currency}</Text>
          </TableCellLayout>
        )
      }),
      createTableColumn<HistoryRow>({
        columnId: 'previousValue',
        compare: (a, b) => a.previousValue - b.previousValue,
        renderHeaderCell: () => t('conceptValueHistoryChart.columns.previousValue', 'Valor anterior'),
        renderCell: (item) => (
          <TableCellLayout>
            <Text size={200} strikethrough style={{ color: 'var(--colorNeutralForeground4)' }}>
              {formatCost(item.previousValue, item.currency)}
            </Text>
          </TableCellLayout>
        )
      }),
      createTableColumn<HistoryRow>({
        columnId: 'newValue',
        compare: (a, b) => a.newValue - b.newValue,
        renderHeaderCell: () => t('conceptValueHistoryChart.columns.newValue', 'Nuevo valor'),
        renderCell: (item) => (
          <TableCellLayout>
            <Text size={200} weight="semibold">{formatCost(item.newValue, item.currency)}</Text>
          </TableCellLayout>
        )
      }),
      createTableColumn<HistoryRow>({
        columnId: 'variance',
        compare: (a, b) => a.variancePercentage - b.variancePercentage,
        renderHeaderCell: () => t('conceptValueHistoryChart.columns.variance', 'Variación'),
        renderCell: (item) => (
          <TableCellLayout>
            {item.variancePercentage === 0 ? (
              <Text size={200}>—</Text>
            ) : (
              <Badge
                appearance="tint"
                color={item.variancePercentage > 0 ? 'success' : 'danger'}
                size="medium"
                icon={item.variancePercentage > 0 ? <ArrowTrendingFilled /> : <ArrowTrendingDownFilled />}
              >
                {item.variancePercentage > 0 ? '+' : ''}{item.variancePercentage.toFixed(1)}%
              </Badge>
            )}
          </TableCellLayout>
        )
      })
    ],
    [t]
  );

  if (histories.length === 0) {
    return (
      <div className={styles.emptyState}>
        <Text>{t('conceptValueHistoryChart.empty', 'Sin historial de cambios disponible.')}</Text>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      {/* Chart */}
      <div className={styles.chartWrapper}>
        <ReactECharts
          notMerge
          lazyUpdate
          option={chartOption}
          style={{ height: '260px', width: '100%' }}
        />
      </div>

      {/* Grid */}
      <div className={styles.gridWrapper}>
        <DataGrid
          items={rows}
          columns={columns}
          sortable
          getRowId={(item) => String(item.id)}
          focusMode="composite"
          resizableColumns
          columnSizingOptions={{
            date:          { idealWidth: 120, minWidth: 100 },
            currency:      { idealWidth: 80,  minWidth: 70 },
            previousValue: { idealWidth: 160, minWidth: 120 },
            newValue:      { idealWidth: 160, minWidth: 120 },
            variance:      { idealWidth: 130, minWidth: 100 }
          }}
          style={{ width: '100%' }}
        >
          <DataGridHeader>
            <DataGridRow>
              {({ renderHeaderCell }) => (
                <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>
              )}
            </DataGridRow>
          </DataGridHeader>
          <DataGridBody<HistoryRow>>
            {({ item, rowId }) => (
              <DataGridRow<HistoryRow> key={rowId}>
                {({ renderCell }) => <DataGridCell>{renderCell(item)}</DataGridCell>}
              </DataGridRow>
            )}
          </DataGridBody>
        </DataGrid>
      </div>
    </div>
  );
};

export default ConceptValueHistoryChartComponent;
